import { cached, tracked } from "@glimmer/tracking";
import EmberObject, { computed, get, set } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { trackedArray } from "@ember/reactive/collections";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { isEmpty } from "@ember/utils";
import {
  arraySortedByProperties,
  removeValueFromArray,
} from "discourse/lib/array-tools";
import { AUTO_GROUPS } from "discourse/lib/constants";
import deprecated, { withSilencedDeprecations } from "discourse/lib/deprecated";
import { isRailsTesting, isTesting } from "discourse/lib/environment";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import Mobile from "discourse/lib/mobile";
import PreloadStore from "discourse/lib/preload-store";
import singleton from "discourse/lib/singleton";
import { autoTrackedArray } from "discourse/lib/tracked-tools";
import Archetype from "discourse/models/archetype";
import Category from "discourse/models/category";
import PostActionType from "discourse/models/post-action-type";
import RestModel from "discourse/models/rest";
import TrustLevel from "discourse/models/trust-level";

@singleton
export default class Site extends RestModel {
  static createCurrent() {
    const store = getOwnerWithFallback(this).lookup("service:store");
    const siteAttributes = PreloadStore.get("site");
    siteAttributes["isReadOnly"] = PreloadStore.get("isReadOnly");
    siteAttributes["isStaffWritesOnly"] = PreloadStore.get("isStaffWritesOnly");
    return store.createRecord("site", siteAttributes);
  }

  static create() {
    const result = super.create.apply(this, arguments);

    if (result.categories) {
      result.categories = result.categories.map((c) => {
        return result.store.createRecord("category", c);
      });
    }

    if (result.trust_levels) {
      result.trustLevels = Object.entries(result.trust_levels).map(
        ([key, id]) => {
          return new TrustLevel(id, key);
        }
      );
      delete result.trust_levels;
    }

    if (result.post_action_types) {
      result.postActionByIdLookup = EmberObject.create();
      result.post_action_types = result.post_action_types.map((p) => {
        const actionType = PostActionType.create(p);
        result.postActionByIdLookup.set("action" + p.id, actionType);
        return actionType;
      });
    }

    if (result.topic_flag_types) {
      result.topicFlagByIdLookup = EmberObject.create();
      result.topic_flag_types = result.topic_flag_types.map((p) => {
        const actionType = PostActionType.create(p);
        result.topicFlagByIdLookup.set("action" + p.id, actionType);
        return actionType;
      });
    }

    if (result.archetypes) {
      result.archetypes = result.archetypes.map((a) => {
        a.site = result;
        return Archetype.create(a);
      });
    }

    if (result.user_fields) {
      result.user_fields = result.user_fields.map((uf) =>
        EmberObject.create(uf)
      );
    }

    return result;
  }

  @service siteSettings;
  @service capabilities;

  @tracked topicCountDesc = ["topic_count:desc"];
  @autoTrackedArray categories = [];
  @autoTrackedArray groups = [];

  #siteInitialized = false;

  @computed("is_readonly")
  get isReadOnly() {
    return this.is_readonly;
  }

  set isReadOnly(value) {
    set(this, "is_readonly", value);
  }

  get categoriesByCount() {
    return arraySortedByProperties(this.categories, this.topicCountDesc);
  }

  get groupsById() {
    const map = {};
    Object.values(AUTO_GROUPS).forEach((g) => (map[g.id] = g));
    this.groups?.forEach((g) => (map[g.id] = g));
    return map;
  }

  @dependentKeyCompat
  get desktopView() {
    return !this.mobileView;
  }

  @dependentKeyCompat
  get mobileView() {
    this.#siteInitialized ||= getOwnerWithFallback(this).lookup(
      "-application-instance:main"
    )?._booted;

    if (!this.#siteInitialized) {
      if (isTesting() || isRailsTesting()) {
        throw new Error(
          "Accessing `site.mobileView` or `site.desktopView` during the site initialization phase. " +
            "Move these checks to a component, transformer, or API callback that executes during page rendering."
        );
      }

      deprecated(
        "Accessing `site.mobileView` or `site.desktopView` during the site initialization " +
          "can lead to errors and inconsistencies when the browser window is " +
          "resized. Please move these checks to a component, transformer, or API callback that executes during page" +
          " rendering.",
        {
          since: "3.5.0.beta9-dev",
          id: "discourse.static-viewport-initialization",
          url: "https://meta.discourse.org/t/367810",
        }
      );
    }

    if (Mobile.mobileForced) {
      return true;
    }

    return withSilencedDeprecations(
      "discourse.static-viewport-initialization",
      () => !this.capabilities.viewport.sm
    );
  }

  @dependentKeyCompat
  get isMobileDevice() {
    deprecated(
      "Site.isMobileDevice is deprecated. Use `site.mobileView` and `site.desktopView` instead for " +
        "viewport-based values or `capabilities.isMobileDevice` for user-agent based detection.",
      {
        id: "discourse.site.is-mobile-device",
        since: "3.5.0.beta9-dev",
        url: "https://meta.discourse.org/t/367810",
      }
    );

    return this.mobileView;
  }

  @dependentKeyCompat
  get categoriesById() {
    return new Map(this.categories.map((c) => [c.id, c]));
  }

  @computed("categories.@each.parent_category_id")
  get categoriesByParentId() {
    const map = new Map();
    for (const category of this.categories) {
      const siblings = map.get(category.parent_category_id) || [];
      siblings.push(category);
      map.set(category.parent_category_id, siblings);
    }
    return map;
  }

  @computed("notification_types")
  get notificationLookup() {
    const result = [];
    Object.keys(this.notification_types).forEach(
      (k) => (result[this.notification_types[k]] = k)
    );
    return result;
  }

  @computed("post_action_types.[]")
  get flagTypes() {
    const postActionTypes = this.post_action_types;
    if (!postActionTypes) {
      return [];
    }
    return trackedArray(postActionTypes.filter((type) => type.is_flag));
  }

  // Sort subcategories under parents
  @cached
  get sortedCategories() {
    return Category.sortCategories(this.categoriesByCount);
  }

  // Returns it in the correct order, by setting
  @computed("categories.[]")
  get categoriesList() {
    return this.siteSettings.fixed_category_positions
      ? this.categories
      : this.sortedCategories;
  }

  @computed("categories.[]", "categories.@each.notification_level")
  get trackedCategoriesList() {
    const trackedCategories = [];

    for (const category of this.categories) {
      if (category.isTracked) {
        if (
          this.siteSettings.allow_uncategorized_topics ||
          !category.isUncategorizedCategory
        ) {
          trackedCategories.push(category);
        }
      }
    }

    return trackedCategories;
  }

  get topTags() {
    return this.#transformTags(this.top_tags);
  }

  get categoryTopTags() {
    return this.#transformTags(this.category_top_tags);
  }

  groupName(groupId) {
    const group = this.groupsById[groupId];
    return group ? group.name : null;
  }

  groupFullName(groupId) {
    const group = this.groupsById[groupId];
    return group ? group.full_name : null;
  }

  collectUserFields(fields) {
    fields = fields || {};

    let siteFields = this.user_fields;

    if (!isEmpty(siteFields)) {
      return siteFields.map((f) => {
        let value = fields ? fields[f.id.toString()] : null;
        value = value || trustHTML("&mdash;");
        return { name: f.name, value };
      });
    }
    return [];
  }

  postActionTypeById(id) {
    return this.get("postActionByIdLookup.action" + id);
  }

  topicFlagTypeById(id) {
    return this.get("topicFlagByIdLookup.action" + id);
  }

  removeCategory(id) {
    const categories = this.categories;
    const existingCategory = categories.find((c) => c.id === id);
    if (existingCategory) {
      removeValueFromArray(categories, existingCategory);
    }
  }

  updateCategory(newCategory) {
    if (newCategory instanceof Category) {
      throw new Error(
        "updateCategory should be passed a pojo, not a category model instance"
      );
    }

    const categories = this.categories;
    const categoryId = get(newCategory, "id");
    const existingCategory = categories.find((c) => c.id === categoryId);

    if (newCategory.permission === null) {
      delete newCategory.permission;
    }

    if (newCategory.has_children == null) {
      delete newCategory.has_children;
    }

    if (existingCategory) {
      existingCategory.setProperties(newCategory);
      existingCategory.setupCategoryTypes();
      return existingCategory;
    } else {
      newCategory = this.store.createRecord("category", newCategory);
      categories.push(newCategory);
      return newCategory;
    }
  }

  #transformTags(tags) {
    if (!tags) {
      return [];
    }
    return tags.map((tag) => this.store.createRecord("tag", tag));
  }
}

if (typeof Discourse !== "undefined") {
  let warned = false;
  // eslint-disable-next-line no-undef
  Object.defineProperty(Discourse, "Site", {
    get() {
      if (!warned) {
        deprecated("Import the Site class instead of using Discourse.Site", {
          since: "2.4.0",
          id: "discourse.global.site",
        });
        warned = true;
      }
      return Site;
    },
  });
}
